import React, { useState } from 'react';
import { BadgeCheckIcon, CheckCircle2Icon, ClockIcon, FacebookIcon, InstagramIcon, Loader2Icon, MailIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { CONTACT } from '../data/media';
import { Reveal } from './Reveal';
import { SectionHeading } from './SectionHeading';
interface FormState {
  name: string;
  phone: string;
  email: string;
  service: string;
  granite: string;
  message: string;
}
type FormErrors = Partial<Record<keyof FormState, string>>;
const emptyForm: FormState = {
  name: '',
  phone: '',
  email: '',
  service: '',
  granite: '',
  message: ''
};
const fieldClasses = 'w-full rounded border border-black/15 bg-white px-4 py-3 text-sm text-ink transition-colors duration-200 ease-smooth placeholder:text-ink/40 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30';
export function Contact() {
  const {
    t
  } = useLanguage();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const update = (key: keyof FormState, value: string) => {
    setForm(prev => ({
      ...prev,
      [key]: value
    }));
    setErrors(prev => ({
      ...prev,
      [key]: undefined
    }));
  };
  const validate = (): FormErrors => {
    const next: FormErrors = {};
    if (form.name.trim().length < 2) next.name = t.contact.errors.name;
    if (form.phone.replace(/\D/g, '').length < 10) next.phone = t.contact.errors.phone;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) next.email = t.contact.errors.email;
    if (!form.service) next.service = t.contact.errors.service;
    if (form.message.trim().length < 10) next.message = t.contact.errors.message;
    return next;
  };
  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setStatus('submitting');
    await new Promise(resolve => setTimeout(resolve, 1100));
    setStatus('success');
  };
  const info = [{
    id: 'area',
    icon: MapPinIcon,
    label: t.contact.areaLabel,
    value: t.contact.areaValue,
    href: undefined as string | undefined
  }, {
    id: 'phone',
    icon: PhoneIcon,
    label: t.contact.phoneLabel,
    value: CONTACT.phoneDisplay,
    href: `tel:+1${CONTACT.phoneRaw}`
  }, {
    id: 'email',
    icon: MailIcon,
    label: t.contact.emailLabel,
    value: CONTACT.email,
    href: `mailto:${CONTACT.email}`
  }, {
    id: 'license',
    icon: BadgeCheckIcon,
    label: t.contact.licenseLabel,
    value: CONTACT.license,
    href: undefined
  }];
  return <section id="contact" className="w-full bg-white py-16 lg:py-20" data-id="element-28" data-mp="35r47qafhvcd6">
      <div className="mx-auto max-w-shell px-4 sm:px-6 lg:px-8" data-id="element-29" data-mp="30zm75nhdvdsq">
        <SectionHeading title={t.contact.title} subtitle={t.contact.subtitle} data-id="element-30" data-mp="30x9f2hhwvn1y" />

        <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:gap-14" data-id="element-31" data-mp="38c1ustgir834">
          <Reveal data-id="element-32" data-mp="3d9tzyp66cqp9">
            {status === 'success' ? <div className="flex h-full flex-col justify-center rounded border border-brand-500/40 bg-cream p-8 text-center lg:p-12" data-id="element-33" data-mp="3i8eymk5s32bl">
                <CheckCircle2Icon className="mx-auto h-12 w-12 text-brand-500" aria-hidden="true" data-id="element-34" data-mp="3mbl2jl5f4hew" />
                <h3 className="mt-5 font-display text-2xl font-extrabold text-ink" data-id="element-35" data-mp="3r932wn5ddoba">
                  {t.contact.successTitle}
                </h3>
                <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-[#5B5B5B]" data-id="element-36" data-mp="4t8lwtyor7fa">
                  {t.contact.successBody}
                </p>
                <button type="button" onClick={() => {
              setForm(emptyForm);
              setStatus('idle');
            }} className="mx-auto mt-7 rounded border-2 border-ink px-6 py-3 font-display text-xs font-extrabold uppercase tracking-wide text-ink transition-colors duration-200 ease-smooth hover:border-brand-500 hover:bg-brand-500" data-id="element-37" data-mp="9s23axh3fikq">
                  {t.contact.successAgain}
                </button>
              </div> : <form onSubmit={onSubmit} noValidate className="rounded border border-black/10 p-6 shadow-card sm:p-8" data-id="element-38" data-mp="9x13f8ow7p8i">
                <h3 className="font-display text-lg font-extrabold uppercase tracking-wide text-ink" data-id="element-39" data-mp="3iedy98lbl1op">
                  {t.contact.formTitle}
                </h3>

                <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2" data-id="element-40" data-mp="3sb8wrrhlf22z">
                  <div className="sm:col-span-2" data-id="element-41" data-mp="3s95w8ditdqk0">
                    <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-ink/70" data-id="element-42" data-mp="3nbe4pkm306gc">
                      {t.contact.name}
                    </label>
                    <input id="name" name="name" value={form.name} onChange={e => update('name', e.target.value)} aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'name-error' : undefined} className={`mt-2 ${fieldClasses} ${errors.name ? 'border-red-500' : ''}`} data-id="element-43" data-mp="12mq4fm1lrpa4" />
                    {errors.name ? <p id="name-error" className="mt-1.5 text-xs font-semibold text-red-600" data-id="element-44" data-mp="347aq6d79bxn6">
                        {errors.name}
                      </p> : null}
                  </div>

                  <div data-id="element-45" data-mp="2uisozuv2foj0">
                    <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-ink/70" data-id="element-46" data-mp="2uhhny2t08uvl">
                      {t.contact.phone}
                    </label>
                    <input id="phone" name="phone" type="tel" inputMode="tel" placeholder="(805) 000-0000" value={form.phone} onChange={e => update('phone', e.target.value)} aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? 'phone-error' : undefined} className={`mt-2 ${fieldClasses} ${errors.phone ? 'border-red-500' : ''}`} data-id="element-47" data-mp="3n5wpbmj7reog" />
                    {errors.phone ? <p id="phone-error" className="mt-1.5 text-xs font-semibold text-red-600" data-id="element-48" data-mp="3iwm3uxiwnokr">
                        {errors.phone}
                      </p> : null}
                  </div>

                  <div data-id="element-49" data-mp="392dc4c1p7znn">
                    <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-ink/70" data-id="element-50" data-mp="34653zilx56pz">
                      {t.contact.email}
                    </label>
                    <input id="email" name="email" type="email" value={form.email} onChange={e => update('email', e.target.value)} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'email-error' : undefined} className={`mt-2 ${fieldClasses} ${errors.email ? 'border-red-500' : ''}`} data-id="element-51" data-mp="2v4x9zcqfqs1p" />
                    {errors.email ? <p id="email-error" className="mt-1.5 text-xs font-semibold text-red-600" data-id="element-52" data-mp="r085xllqwpm4">
                        {errors.email}
                      </p> : null}
                  </div>

                  <div data-id="element-53" data-mp="10vi6e4whwoxs">
                    <label htmlFor="service" className="block text-xs font-bold uppercase tracking-wider text-ink/70" data-id="element-54" data-mp="10v9uaf875nnn">
                      {t.contact.service}
                    </label>
                    <select id="service" name="service" value={form.service} onChange={e => update('service', e.target.value)} aria-invalid={Boolean(errors.service)} aria-describedby={errors.service ? 'service-error' : undefined} className={`mt-2 ${fieldClasses} ${errors.service ? 'border-red-500' : ''}`} data-id="element-55" data-mp="86baldofedg8">
                      <option value="" data-id="element-56" data-mp="36puklm4d1d2e">{t.contact.servicePlaceholder}</option>
                      {t.services.items.map(service => <option key={service.id} value={service.id} data-id="element-57" data-mp="3gplg9r4npy42">
                          {service.name}
                        </option>)}
                      <option value="other" data-id="element-58" data-mp="3ufyrq3grbuy5">{t.contact.serviceOther}</option>
                    </select>
                    {errors.service ? <p id="service-error" className="mt-1.5 text-xs font-semibold text-red-600" data-id="element-59" data-mp="890fuyq3cdck">
                        {errors.service}
                      </p> : null}
                  </div>

                  <div data-id="element-60" data-mp="mxa8qmux83f8">
                    <label htmlFor="granite" className="block text-xs font-bold uppercase tracking-wider text-ink/70" data-id="element-61" data-mp="mx0fmqekxg5b">
                      {t.contact.granite}
                    </label>
                    <select id="granite" name="granite" value={form.granite} onChange={e => update('granite', e.target.value)} className={`mt-2 ${fieldClasses}`} data-id="element-62" data-mp="cxjbidez7abg">
                      <option value="" data-id="element-63" data-mp="8y0rrkyw55a7">{t.contact.granitePlaceholder}</option>
                      {t.granite.items.map(slab => <option key={slab.id} value={slab.id} data-id="element-64" data-mp="3qb4kpohs0bxr">
                          {slab.name}
                        </option>)}
                    </select>
                  </div>

                  <div className="sm:col-span-2" data-id="element-65" data-mp="3lduvo9yau2ag">
                    <label htmlFor="message" className="block text-xs font-bold uppercase tracking-wider text-ink/70" data-id="element-66" data-mp="3lckzxrex566z">
                      {t.contact.message}
                    </label>
                    <textarea id="message" name="message" rows={5} placeholder={t.contact.messagePlaceholder} value={form.message} onChange={e => update('message', e.target.value)} aria-invalid={Boolean(errors.message)} aria-describedby={errors.message ? 'message-error' : undefined} className={`mt-2 resize-y ${fieldClasses} ${errors.message ? 'border-red-500' : ''}`} data-id="element-67" data-mp="3nq7hky9z9r2i" />
                    {errors.message ? <p id="message-error" className="mt-1.5 text-xs font-semibold text-red-600" data-id="element-68" data-mp="idf2xabosgz">
                        {errors.message}
                      </p> : null}
                  </div>
                </div>

                <button type="submit" disabled={status === 'submitting'} className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded bg-brand-500 px-7 py-4 font-display text-sm font-extrabold uppercase tracking-wide text-ink transition-colors duration-200 ease-smooth hover:bg-brand-400 disabled:cursor-wait disabled:opacity-70 sm:w-auto" data-id="element-69" data-mp="2dqo5gvdsk7op">
                  {status === 'submitting' ? <>
                      <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-70" data-mp="1ptqyfgv3oq7u" />
                      {t.contact.submitting}
                    </> : t.contact.submit}
                </button>
                <p className="mt-4 text-xs leading-relaxed text-ink/50" data-id="element-71" data-mp="1ztho958puh1l">{t.contact.privacy}</p>
              </form>}
          </Reveal>

          <Reveal delay={0.08} data-id="element-72" data-mp="3b7d4khbcwvue">
            <div className="rounded bg-ink p-6 sm:p-8" data-id="element-73" data-mp="3b6afmexlh3h4">
              <h3 className="font-display text-lg font-extrabold uppercase tracking-wide text-white" data-id="element-74" data-mp="3iaa0hsgojn1t">
                {t.contact.infoTitle}
              </h3>
              <ul className="mt-6 space-y-5" data-id="element-75" data-mp="3naxv0o44am0s">
                {info.map(row => <li key={row.id} className="flex gap-4" data-id="element-76" data-mp="3s8pmxi4ri84z">
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded bg-white/10" data-id="element-77" data-mp="11wqssotsz7b">
                      <row.icon className="h-5 w-5 text-brand-500" aria-hidden="true" data-id="element-78" data-mp="2zklewpypxiz4" />
                    </span>
                    <span data-id="element-79" data-mp="34in0pinj3wea">
                      <span className="block text-[11px] font-bold uppercase tracking-wider text-white/45" data-id="element-80" data-mp="39hgfdeejxtg1">
                        {row.label}
                      </span>
                      {row.href ? <a href={row.href} className="font-display text-base font-bold text-white transition-colors duration-200 ease-smooth hover:text-brand-500" data-id="element-81" data-mp="2gt10qszc1d58">
                          {row.value}
                        </a> : <span className="font-display text-base font-bold text-white" data-id="element-82" data-mp="yw7d9lw178mx">{row.value}</span>}
                    </span>
                  </li>)}
                <li className="flex gap-4" data-id="element-83" data-mp="ovx2e7xgydax">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded bg-white/10" data-id="element-84" data-mp="oz2ulw1e9vav">
                    <ClockIcon className="h-5 w-5 text-brand-500" aria-hidden="true" data-id="element-85" data-mp="f76tec40hp2f" />
                  </span>
                  <span data-id="element-86" data-mp="b5bpr0ylyzlh">
                    <span className="block text-[11px] font-bold uppercase tracking-wider text-white/45" data-id="element-87" data-mp="b6mv357pcfep">
                      {t.contact.hoursLabel}
                    </span>
                    {t.contact.hours.map(line => <span key={line} className="block text-sm font-semibold text-white/85" data-id="element-88" data-mp="19i6yzuaq9v9">
                        {line}
                      </span>)}
                  </span>
                </li>
              </ul>

              <div className="mt-7 overflow-hidden rounded border border-white/10" data-id="element-89" data-mp="21a6msyytwvgv">
                <iframe title={t.contact.mapLabel} src="https://www.openstreetmap.org/export/embed.html?bbox=-119.55%2C34.10%2C-118.70%2C34.45&layer=mapnik" className="h-56 w-full" loading="lazy" data-id="element-90" data-mp="1mkqxlney4y3a" />
              </div>

              <div className="mt-7 border-t border-white/10 pt-6" data-id="element-91" data-mp="1iivzssw852u9">
                <p className="text-[11px] font-bold uppercase tracking-wider text-white/45" data-id="element-92" data-mp="1ih2p92ihpygz">
                  {t.contact.followLabel}
                </p>
                <div className="mt-3 flex gap-3" data-id="element-93" data-mp="18k6hx04bvie0">
                  {[{
                  id: 'facebook',
                  Icon: FacebookIcon,
                  href: 'https://facebook.com'
                }, {
                  id: 'instagram',
                  Icon: InstagramIcon,
                  href: 'https://instagram.com'
                }].map(({
                  id,
                  Icon,
                  href
                }) => <a key={id} href={href} target="_blank" rel="noreferrer" aria-label={id} className="inline-flex h-11 w-11 items-center justify-center rounded border border-white/15 text-brand-500 transition-colors duration-200 ease-smooth hover:border-brand-500 hover:bg-brand-500 hover:text-ink" data-id="element-94" data-mp="rcp0gkzz1xup">
                      <Icon className="h-5 w-5" aria-hidden="true" data-id="element-95" data-mp="160jvw95d6gbh" />
                    </a>)}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>;
}